import type { Session } from "../core/session";
import { el, formatClock, on } from "./dom";

/** Floating controls while a take is running. */
export function mountPill(session: Session): { sync: () => void } {
  const pill = el<HTMLDivElement>("pill");
  const time = el<HTMLSpanElement>("pill-time");
  const flag = el<HTMLSpanElement>("pill-flag");
  const micButton = el<HTMLButtonElement>("pill-mic");
  const cameraButton = el<HTMLButtonElement>("pill-camera");
  const pauseButton = el<HTMLButtonElement>("pill-pause");
  const pauseLabel = el<HTMLSpanElement>("pill-pause-label");
  const pauseGlyph = pauseButton.querySelector<HTMLSpanElement>(".pill-glyph");
  const micLabel = el<HTMLSpanElement>("pill-mic-label");
  const cameraLabel = el<HTMLSpanElement>("pill-camera-label");
  const stopButton = el<HTMLButtonElement>("pill-stop");

  on(micButton, "click", () => {
    session.setMicMuted(!session.micMuted);
    sync();
  });
  on(cameraButton, "click", () => {
    session.hideCameraLive(!session.cameraHiddenLive);
    sync();
  });
  on(pauseButton, "click", () => {
    if (session.phase === "recording") session.pause();
    else if (session.phase === "paused") session.resume();
    sync();
  });
  on(stopButton, "click", () => {
    void session.stop();
  });

  function sync(): void {
    const recording = session.isRecording;
    pill.hidden = !recording;
    time.textContent = formatClock(session.elapsedMs);
    flag.hidden = session.phase !== "paused";
    const paused = session.phase === "paused";
    pauseLabel.textContent = paused ? "Resume" : "Pause";
    if (pauseGlyph) pauseGlyph.textContent = paused ? "▶" : "❚❚";
    pauseButton.title = paused ? "Resume recording" : "Pause recording";
    pauseButton.classList.toggle("is-attention", paused);

    micButton.setAttribute("aria-pressed", String(session.micMuted));
    micLabel.textContent = session.micMuted ? "Mic muted" : "Mic on";
    micButton.title = session.micMuted ? "Unmute microphone" : "Mute microphone";

    cameraButton.hidden = !session.hasCamera;
    cameraButton.setAttribute("aria-pressed", String(session.cameraHiddenLive));
    cameraLabel.textContent = session.cameraHiddenLive ? "Show camera" : "Hide camera";
    cameraButton.title = session.cameraHiddenLive ? "Show the camera again" : "Hide the camera";
  }

  session.events.on("tick", () => {
    if (session.isRecording) sync();
  });
  session.events.on("phase", sync);
  session.events.on("sources", sync);
  sync();
  return { sync };
}
